import React, { useState, useEffect } from "react";
import {
  Box,
  Dialog,
  Divider,
  TextField,
  Typography,
  createTheme,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import "../modals/modal.css";
import CustomButton from "../../reusableComponents/CustomButton";
import "../reports/reports.css";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";
import { useAuth } from "../../context/API_Client";

export default function DateTimeModal({ openModal, onClose, updateSelect }) {
  const { merchantSettings } = useAuth();
  const [error, setError] = useState("");
  const [isSubmit, setIsSubmit] = useState(false);
  const [timeZone, setTimeZone] = useState(null);
  const getRoundedTime = (date) => {
    const minutes = date.minute();
    const remainder = minutes % 15;
    if (remainder === 0) {
      return date;
    } else {
      return date.add(15 - remainder, "minute");
    }
  };

  const [selectedDate, setSelectedDate] = useState(
    getRoundedTime(dayjs().add(2, "day"))
  );

  useEffect(() => {
    merchantSettings(
      function (data) {
        setTimeZone(data?.timeZone);
      },
      function (errorMsg) { }
    );
  }, []);

  const handleDateChange = (newValue) => {
    setSelectedDate(newValue);
    setError("");
  };

  const handleTimeChange = (newValue) => {
    const now = dayjs();
    if (selectedDate.isSame(now, "day")) {
      const diffInMinutes = newValue.diff(now, "minute");
      if (diffInMinutes < 15 && diffInMinutes < now) {
        setError("Invalid time.");
        return;
      }
    }
    setSelectedDate(newValue);
    setError("");
  };

  const disablePastTimes = (time) => {
    const now = dayjs();
    if (selectedDate.isSame(now, "day")) {
      const currentHour = now.hour();
      const selectedHour = time.hour();
      if (selectedHour < currentHour) {
        return true;
      } else if (selectedHour === currentHour) {
        return time.minute() < now.add(15, "minute").minute();
      }
    }
    return false;
  };

  const disableAmPm = (amOrPm) => {
    const now = dayjs();
    if (selectedDate.isSame(now, "day")) {
      if (now.hour() >= 12 && amOrPm === "am") {
        return true;
      }
      if (now.hour() < 12 && amOrPm === "pm") {
        return true;
      }
    }
    return false;
  };

  const disablePastDates = (date) => {
    const today = dayjs();
    const oneYearFromToday = today.add(1, "year");
    return date.isBefore(today, "day") || date.isAfter(oneYearFromToday, "day");
  };

  const handleCloseModal = () => {
    onClose();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      onClose("cancel");
    }
  };
  useEffect(() => {
    handleTimeChange(selectedDate);
  }, [selectedDate, isSubmit]);

  const handleSubmit = () => {
    setIsSubmit(true);
    if (!error) {
      onClose("apply");
      const now = dayjs();
      const differenceInSeconds = selectedDate.diff(now, "second");
      const formattedDate =
        selectedDate.format("MMM DD, YYYY, h:mm A,") + " " + timeZone;
      updateSelect("customExpiryTime", {
        title: formattedDate,
        value: differenceInSeconds,
      });
    } else {
      setError(error);
    }
  };

  return (
    <>
      <Dialog
        className="dateTimeModal"
        open={openModal}
        onKeyDown={handleKeyDown}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "6px",
            padding: "24px 24px 0px 24px",
          }}
        >
          <Typography className="SemiboldTwenty font-black-highest">
            Date & Time
          </Typography>
        </div>
        <Typography
          className="labelRegular font-black-high"
          style={{ padding: "0px 24px" }}
        >
          Date and time are displayed here in {timeZone} format
        </Typography>
        <Divider style={{ margin: "15px 25px -6px 25px" }} />
        <div className="displayTimeDirectionRow">
          <div
            className="dateTimeRange"
            style={{ height: error ? "370px" : "360px" }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "11px",
                }}
              >
                <DateCalendar
                  value={selectedDate}
                  onChange={handleDateChange}
                  shouldDisableDate={disablePastDates}
                  views={["year", "month"]}
                  // views={["year", "month", "day"]}
                  // openTo="day"
                  openTo="month"
                  minDate={dayjs()}
                  maxDate={dayjs().add(1, "year")}
                />

                <TimePicker
                  value={dayjs(selectedDate)}
                  onChange={(newValue) => handleTimeChange(newValue)}
                  shouldDisableTime={disablePastTimes}
                  ampm={disableAmPm}
                  timeSteps={{ minutes: 15 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={Boolean(error)}
                      helperText={error}
                    />
                  )}
                />
                {isSubmit == true && error !== "" ? (
                  <div
                    style={{
                      color: "#E12121",
                      fontFamily: "Poppins",
                      fontSize: "12px",
                      fontWeight: "400",
                      lineHeight: "18px",
                      marginLeft: "10px",
                      marginTop: "-10px",
                    }}
                  >
                    {error}
                  </div>
                ) : null}
              </Box>
            </LocalizationProvider>
          </div>
        </div>
        <div
          style={{
            paddingBottom: "24px",
            paddingTop: "24px",
            display: "flex",
            justifyContent: "end",
            paddingRight: "24px",
            boxShadow: "0px -2px 8px 0px rgba(39, 39, 39, 0.15)",
          }}
        >
          <CustomButton
            variant={"outlined"}
            size={"sm"}
            type={"secondary"}
            text={"Cancel"}
            onClick={() => onClose("cancel")}
          />
          <div style={{ marginLeft: "8px" }}>
            <CustomButton
              variant={"contained"}
              size={"sm"}
              type={"secondary"}
              text={"Apply"}
              onClick={handleSubmit}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
}

DateTimeModal.defaultProps = {
  openModal: false,
  sendDataFromDateModal: () => { },
  onClose: () => { },
  presentData: [],
};
